import type { FC, ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'

type Props = {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  wide?: boolean
}

const DashboardModal: FC<Props> = ({ open, onClose, title, children, wide }) => {
  const backdropRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      ref={backdropRef}
      className='fixed inset-0 z-[1050] flex items-start justify-center pt-10 pb-10 overflow-y-auto'
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
      onClick={(e) => { if (e.target === backdropRef.current) onClose() }}
    >
      <div
        className={`relative bg-white rounded-2xl shadow-xl border border-slate-200 mx-4 w-full ${wide ? 'max-w-4xl' : 'max-w-lg'}`}
        style={{ animation: 'modalFadeIn 0.2s ease-out' }}
      >
        {/* Header */}
        <div className='flex items-center justify-between px-6 py-4 border-b border-slate-100'>
          <h3 className='text-base font-bold text-slate-900'>{title}</h3>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light'
            onClick={onClose}
            aria-label='Close'
          >
            <X className='w-5 h-5' />
          </button>
        </div>

        {/* Body */}
        <div className='px-6 py-4 max-h-[70vh] overflow-y-auto'>{children}</div>
      </div>
    </div>
  )
}

export default DashboardModal
